/**
 * Selettori: funzioni pure che derivano dati "pronti da mostrare" dallo stato.
 *
 * Tenendo filtro e ordinamento fuori dal codice di rendering, la stessa logica
 * è testabile senza DOM e la UI si limita a disegnare ciò che riceve.
 */

/** Criteri di ordinamento supportati. */
export const SORT_OPTIONS = Object.freeze({
  NEWEST: 'newest',
  OLDEST: 'oldest',
  SCORE: 'score',
  COMMENTS: 'comments',
});

/**
 * Filtra le news in base al testo cercato (titolo, autore o dominio).
 * La ricerca è case-insensitive e ignora gli spazi ai bordi.
 *
 * @param {Array<object>} stories
 * @param {string} query
 * @returns {Array<object>}
 */
export function filterBySearch(stories = [], query = '') {
  const term = String(query ?? '').trim().toLowerCase();
  if (!term) return [...stories];

  return stories.filter((story) => {
    const haystack = [story?.title, story?.author, story?.domain]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(term);
  });
}

/**
 * Tiene solo le news presenti tra i preferiti.
 *
 * @param {Array<{id:number}>} stories
 * @param {Array<number>} favoriteIds
 * @returns {Array<object>}
 */
export function filterByFavorites(stories = [], favoriteIds = []) {
  const favorites = new Set(favoriteIds);
  return stories.filter((story) => favorites.has(story?.id));
}

/**
 * Ordina le news secondo il criterio scelto (senza mutare l'array originale).
 *
 * @param {Array<object>} stories
 * @param {string} [sort=SORT_OPTIONS.NEWEST]
 * @returns {Array<object>}
 */
export function sortStories(stories = [], sort = SORT_OPTIONS.NEWEST) {
  const copy = [...stories];

  switch (sort) {
    case SORT_OPTIONS.OLDEST:
      return copy.sort((a, b) => (a?.time ?? 0) - (b?.time ?? 0));
    case SORT_OPTIONS.SCORE:
      return copy.sort((a, b) => (b?.score ?? 0) - (a?.score ?? 0));
    case SORT_OPTIONS.COMMENTS:
      return copy.sort((a, b) => (b?.comments ?? 0) - (a?.comments ?? 0));
    case SORT_OPTIONS.NEWEST:
    default:
      return copy.sort((a, b) => (b?.time ?? 0) - (a?.time ?? 0));
  }
}

/**
 * Pipeline completa: preferiti → ricerca → ordinamento.
 * È ciò che la lista mostra a video.
 *
 * @param {object} state stato dello store
 * @returns {Array<object>}
 */
export function selectVisibleStories(state) {
  const { stories = [], search = '', sort, favorites = [], showFavoritesOnly = false } = state ?? {};

  const base = showFavoritesOnly ? filterByFavorites(stories, favorites) : stories;
  return sortStories(filterBySearch(base, search), sort);
}

/**
 * @param {object} state
 * @param {number} id
 * @returns {boolean}
 */
export function isFavorite(state, id) {
  return Array.isArray(state?.favorites) && state.favorites.includes(id);
}

/**
 * Aggiunge o rimuove un ID dall'elenco dei preferiti (funzione pura).
 *
 * @param {Array<number>} favorites
 * @param {number} id
 * @returns {Array<number>} nuovo array
 */
export function toggleFavoriteId(favorites = [], id) {
  return favorites.includes(id) ? favorites.filter((fav) => fav !== id) : [...favorites, id];
}
